import React from "react";

import {
  faArchive,
  faBell,
  faBooks,
  faChartLineUp,
  faCircleNotch,
  faCodePullRequestDraft,
  faCog,
  faExchange,
  faHdd,
  faRadar,
  faRocket,
  faTable,
  faWrench,
} from "@fortawesome/pro-solid-svg-icons";

import { defaultServerHumanId } from "utils/serverGroups";
import { useRoutes } from "utils/routes";

import {
  useCurrentOrganization,
  useCurrentServerGroup,
  useFallbackServerId,
} from "providers/WithCurrentOrganization";
import NavItem from "../NavItem";

type Props = {
  organizationSlug?: string;
  serverId?: string;
  databaseId?: string;
  serverGroupId?: string;
};

const NavItems: React.FunctionComponent<Props> = ({
  organizationSlug,
  databaseId,
  serverId,
  serverGroupId,
}) => {
  const {
    root,
    database,
    databaseIndexAdvisor,
    databaseQueries,
    databaseQueriesAdvisor,
    databaseWorkbooks,
    organizationWorkbooks,
    serverGroupWorkbooks,
    databaseTables,
    databaseLogs,
    databaseBackends,
    databaseChecks,
    server,
    serverQueries,
    serverQueriesAdvisor,
    serverWorkbooks,
    serverSchema,
    serverIndexAdvisor,
    serverLogs,
    serverConnections,
    serverVacuums,
    serverConfig,
    serverSystem,
    serverEdit,
    serverChecks,
    serverGroupServers,
    serverGroupQueries,
    serverGroupEdit,
    organization,
    organizationServersSettings,
    organizationServerGroupsSettings,
    organizationChecks,
  } = useRoutes();

  const fallbackServerId = useFallbackServerId();
  const currentServerGroup = useCurrentServerGroup(true);
  const fallbackServerGroupServerId = currentServerGroup
    ? defaultServerHumanId(currentServerGroup)
    : undefined;

  const currentOrganization = useCurrentOrganization(true);
  const serverGroupCrudEnabled =
    currentOrganization?.enabledFeatures.serverGroupCrud ?? false;
  const hasServerGroups = (currentOrganization?.serverGroups.length ?? 0) > 0;

  if (!organizationSlug && !databaseId && !serverId && !serverGroupId) {
    return (
      <NavItem
        to={root()}
        icon={faRocket}
        matchPaths={["/", "/organizations", "/servers", "/databases"]}
      >
        Dashboard
      </NavItem>
    );
  }

  const pickRoute = (
    serverRoute: (serverId: string) => string,
    databaseRoute?: (databaseId: string) => string,
    organizationRoute?: (organizationSlug: string) => string,
    serverGroupRoute?: (serverGroupId: string) => string,
  ) => {
    return databaseId && databaseRoute
      ? databaseRoute(databaseId)
      : serverGroupId && serverGroupRoute
      ? serverGroupRoute(serverGroupId)
      : serverGroupId && serverGroupCrudEnabled && fallbackServerGroupServerId
      ? serverRoute(fallbackServerGroupServerId)
      : serverId
      ? serverRoute(serverId)
      : organizationRoute
      ? organizationRoute(organizationSlug)
      : fallbackServerId
      ? serverRoute(fallbackServerId)
      : organization(organizationSlug);
  };

  return (
    <>
      <NavItem
        to={
          databaseId
            ? database(databaseId)
            : serverGroupId
            ? serverGroupServers(serverGroupId)
            : serverId
            ? server(serverId)
            : organization(organizationSlug)
        }
        icon={faRocket}
        matchPaths={[
          "/organizations/:orgSlug",
          "/organizations/:orgSlug/servers",
          "/organizations/:orgSlug/databases",
          "/organizations/:orgSlug/servergroups",
          "/servers/:serverId",
          "/databases/:databaseId",
          "/servergroups/:serverGroupId/servers",
          "/servergroups/:serverGroupId/databases",
        ]}
      >
        Dashboard
      </NavItem>
      <NavItem
        to={pickRoute(
          serverQueries,
          databaseQueries,
          undefined,
          serverGroupQueries,
        )}
        icon={faChartLineUp}
        matchPaths={[
          "/servers/:serverId/queries",
          "/servergroups/:serverGroupId/queries",
          "/databases/:databaseId/queries",
          "/databases/:databaseId/queries/:queryId/*",
          "/queries/:fingerprint",
        ]}
        matchPathsExclude={[
          "/databases/:databaseId/queries/advisor",
          "/databases/:databaseId/queries/advisor/*",
        ]}
      >
        Query Performance
      </NavItem>
      <NavItem
        to={pickRoute(serverQueriesAdvisor, databaseQueriesAdvisor)}
        icon={faCodePullRequestDraft}
        matchPaths={[
          "/servers/:serverId/queries/advisor",
          "/databases/:databaseId/queries/advisor",
          "/databases/:databaseId/queries/advisor/*",
        ]}
      >
        Query Advisor
      </NavItem>
      <NavItem
        to={pickRoute(serverIndexAdvisor, databaseIndexAdvisor)}
        icon={faRadar}
        matchPaths={[
          "/databases/:databaseId/indexadvisor",
          "/databases/:databaseId/indexadvisor/indexing_engine",
          "/databases/:databaseId/indexadvisor/index_unused",
          "/databases/:databaseId/indexadvisor/config",
          "/databases/:databaseId/indexadvisor/status",
        ]}
      >
        Index Advisor
      </NavItem>
      <NavItem
        to={pickRoute(serverVacuums)}
        icon={faCircleNotch}
        matchPaths={["/servers/:serverId/vacuums/*"]}
      >
        VACUUM Advisor
      </NavItem>
      <NavItem
        to={pickRoute(
          serverWorkbooks,
          databaseWorkbooks,
          organizationWorkbooks,
          serverGroupWorkbooks,
        )}
        icon={faBooks}
        matchPaths={[
          "/organizations/:orgSlug/workbooks",
          "/organizations/:orgSlug/workbooks/*",
          "/servergroups/:serverGroupId/workbooks",
          "/servergroups/:serverGroupId/workbooks/*",
          "/servers/:serverId/workbooks",
          "/databases/:databaseId/workbooks",
          "/servers/:serverId/workbooks/*",
          "/databases/:databaseId/workbooks/*",
        ]}
      >
        Workbooks
      </NavItem>
      <NavItem
        to={pickRoute(serverSchema, databaseTables)}
        icon={faTable}
        matchPaths={[
          "/servers/:serverId/schema",
          "/databases/:databaseId/tables/*",
          "/databases/:databaseId/indexes/*",
          "/databases/:databaseId/views/*",
          "/databases/:databaseId/functions/*",
        ]}
      >
        Schema Statistics
      </NavItem>
      <NavItem
        to={pickRoute(serverLogs, databaseLogs)}
        icon={faArchive}
        matchPaths={[
          "/servers/:serverId/logs/*",
          "/databases/:databaseId/logs/*",
        ]}
      >
        Log Insights
      </NavItem>
      <NavItem
        to={pickRoute(serverConnections, databaseBackends)}
        icon={faExchange}
        matchPaths={[
          "/servers/:serverId/connections",
          "/databases/:databaseId/backends/*",
          "/databases/:databaseId/wait_events",
        ]}
      >
        Connections
      </NavItem>
      <NavItem
        to={pickRoute(serverConfig)}
        icon={faWrench}
        matchPaths={["/servers/:serverId/config/*"]}
      >
        Config Settings
      </NavItem>
      <NavItem
        to={pickRoute(serverSystem)}
        icon={faHdd}
        matchPaths={[
          "/servers/:serverId/system/*",
          "/servers/:serverId/roles/*",
          "/servers/:serverId/replication/*",
        ]}
      >
        System
      </NavItem>
      <NavItem
        to={pickRoute(serverChecks, databaseChecks, organizationChecks)}
        icon={faBell}
        matchPaths={[
          "/organizations/:orgSlug/alerts/*",
          "/organizations/:orgSlug/checks/*",
          "/servers/:serverId/alerts/*",
          "/servers/:serverId/checks/*",
          "/databases/:databaseId/alerts/*",
          "/databases/:databaseId/checks/*",
        ]}
      >
        Alerts & Check-Up
      </NavItem>
      <NavItem
        to={pickRoute(
          serverEdit,
          null,
          serverGroupCrudEnabled && hasServerGroups
            ? organizationServerGroupsSettings
            : organizationServersSettings,
          serverGroupEdit,
        )}
        icon={faCog}
        matchPaths={[
          "/servers/:serverId/edit",
          "/servers/:serverId/snapshots/*",
          "/servers/:serverId/collector_settings/*",
          "/servergroups/:serverGroupId/edit",
          "/organizations/:orgSlug/servers/settings/*",
          "/organizations/:orgSlug/servergroups/settings",
          "/organizations/:orgSlug/servergroups/settings/new",
          "/organizations/:orgSlug/members/*",
          "/organizations/:orgSlug/roles/*",
          "/organizations/:orgSlug/api/*",
          "/organizations/:orgSlug/subscription/*",
          "/organizations/:orgSlug/edit/*",
        ]}
      >
        Settings
      </NavItem>
    </>
  );
};

export default NavItems;
